import { inject } from '@angular/core';
import * as AlertActions from '@curbnturf/alert/src/lib/+state/alert.actions';
import { IBoondockRating, IGuestRating, IListingRating, IPoiRating, NGRX_NO_ACTION, Status } from '@curbnturf/entities';
import * as NetworkSelectors from '@curbnturf/network/src/lib/network/+state/network.selectors';
import { ReservationActionTypes } from '@curbnturf/reservation/src/lib/+state/reservation.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { CaretakerService } from '../caretaker.service';
import { RatingService } from '../rating.service';
import * as RatingActions from './rating.actions';

export const ratingLoadError$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(RatingActions.ratingLoadError),
      switchMap((action) => {
        const message = action?.error?.message || action?.error?.error?.message || JSON.stringify(action?.error);
        return of(
          AlertActions.displayAlert({
            level: 'error',
            title: 'Error Occurred',
            body: `And error occurred while submitting your ratings: ${message}`,
          }),
        );
      }),
    ),
  { functional: true },
);

export const loadGuestRatings$ = createEffect(
  (actions$ = inject(Actions), ratingService = inject(RatingService)) =>
    actions$.pipe(
      ofType(RatingActions.loadGuestRatings),
      switchMap((action) =>
        ratingService.fetchGuestRatings(action.guestId).pipe(
          map((ratings: IGuestRating[]) => RatingActions.guestRatingsLoaded({ ratings })),
          catchError((error) => of(RatingActions.ratingLoadError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadSiteRatings$ = createEffect(
  (actions$ = inject(Actions), ratingService = inject(RatingService)) =>
    actions$.pipe(
      ofType(RatingActions.loadListingRatings),
      switchMap((action) =>
        ratingService.fetchListingRatings(action.listingId).pipe(
          map((ratings: IListingRating[]) => RatingActions.listingRatingsLoaded({ ratings })),
          catchError((error) => of(RatingActions.ratingLoadError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadBoondockRatings$ = createEffect(
  (actions$ = inject(Actions), ratingService = inject(RatingService)) =>
    actions$.pipe(
      ofType(RatingActions.loadBoondockRatings),
      switchMap((action) =>
        ratingService.fetchBoondockRatings(action.boondockId).pipe(
          map((ratings: IBoondockRating[]) => RatingActions.boondockRatingsLoaded({ ratings })),
          catchError((error) => of(RatingActions.ratingLoadError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadPoiRatings$ = createEffect(
  (actions$ = inject(Actions), ratingService = inject(RatingService)) =>
    actions$.pipe(
      ofType(RatingActions.loadPoiRatings),
      switchMap((action) =>
        ratingService.fetchPoiRatings(action.poiId).pipe(
          map((ratings: IPoiRating[]) => RatingActions.poiRatingsLoaded({ ratings })),
          catchError((error) => of(RatingActions.ratingLoadError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const createSiteRating$ = createEffect(
  (actions$ = inject(Actions), ratingService = inject(RatingService)) =>
    actions$.pipe(
      ofType(RatingActions.createListingRatings),
      switchMap((action) =>
        ratingService.createListingRatings(action.ratings).pipe(
          mergeMap((ratings: IListingRating[]) => [
            RatingActions.listingRatingsLoaded({ ratings }),
            RatingActions.listingRatingsCreated({ ratings }),
            AlertActions.displayAlert({
              title: 'Rating Submitted',
              body: `Thank you for submitting your rating. Your feedback helps others make informed decisions.`,
            }),
          ]),
          catchError((error) =>
            [
              RatingActions.listingRatingsFailed({ error })
            ],
          ),
        ),
      ),
      catchError((error) =>
        [
          RatingActions.listingRatingsFailed({ error ,}),
        ],
      ),
    ),
  { functional: true },
);

export const siteRatingsCreated$ = createEffect(
  (actions$ = inject(Actions), store$ = inject(Store)) =>
    actions$.pipe(
      ofType(RatingActions.listingRatingsCreated),
      withLatestFrom(store$.select(NetworkSelectors.getStatus)),
      switchMap(([action, status]) => {
        if (!status?.connected) {
          return of({
            type: ReservationActionTypes.UpdateReservationStatus,
            payload: { reservationId: action, status: Status.rated },
          });
        } else {
          return of(NGRX_NO_ACTION);
        }
      }),
    ),
  { functional: true },
);

export const createGuestRating$ = createEffect(
  (actions$ = inject(Actions), ratingService = inject(RatingService)) =>
    actions$.pipe(
      ofType(RatingActions.createGuestRatings),
      switchMap((action) =>
        ratingService.createGuestRatings(action.ratings).pipe(
          mergeMap((ratings: IGuestRating[]) => [
            RatingActions.guestRatingsLoaded({ ratings }),
            RatingActions.guestRatingsCreated(),
            AlertActions.displayAlert({
              title: 'Rating Submitted',
              body: `Thank you for submitting your rating. Your feedback helps others make informed decisions.`,
            }),
          ]),
          catchError((error) =>
            of(RatingActions.ratingLoadError({ error })),
          ),
        ),
      ),
      catchError((error) =>
        of(RatingActions.ratingLoadError({ error })),
      ),
    ),
  { functional: true },
);

export const createBoondockRating$ = createEffect(
  (actions$ = inject(Actions), ratingService = inject(RatingService)) =>
    actions$.pipe(
      ofType(RatingActions.createBoondockRatings),
      switchMap((action) =>
        ratingService.createBoondockRatings(action.ratings).pipe(
          mergeMap((ratings: IBoondockRating[]) => [
            RatingActions.boondockRatingsLoaded({ ratings }),
            RatingActions.boondockRatingsCreated(),
            AlertActions.displayAlert({
              title: 'Rating Submitted',
              body: `Thank you for submitting your rating. Your feedback helps others make informed decisions.`,
            }),
          ]),
          catchError((error) =>
            of(RatingActions.ratingLoadError({ error })),
          ),
        ),
      ),
      catchError((error) =>
        of(RatingActions.ratingLoadError({ error })),
      ),
    ),
  { functional: true },
);

export const createPoiRating$ = createEffect(
  (actions$ = inject(Actions), ratingService = inject(RatingService)) =>
    actions$.pipe(
      ofType(RatingActions.createPoiRatings),
      switchMap((action) =>
        ratingService.createPoiRatings(action.ratings).pipe(
          mergeMap((ratings: IPoiRating[]) => [
            RatingActions.poiRatingsLoaded({ ratings }),
            RatingActions.poiRatingsCreated(),
            AlertActions.displayAlert({
              title: 'Rating Submitted',
              body: `Thank you for submitting your rating. Your feedback helps others make informed decisions.`,
            }),
          ]),
          catchError((error) =>
            of(RatingActions.ratingLoadError({ error })),
          ),
        ),
      ),
      catchError((error) =>
        of(RatingActions.ratingLoadError({ error })),
      ),
    ),
  { functional: true },
);

export const selectCaretakerSite$ = createEffect(
  (actions$ = inject(Actions), caretakerService = inject(CaretakerService)) =>
    actions$.pipe(
      ofType(RatingActions.selectCaretakerBoondock),
      switchMap((action) =>
        caretakerService.saveBoondockUpdates(action.caretaker).pipe(
          mergeMap((boondock) => [
            RatingActions.selectCaretakerBoondockComplete({ boondock }),
            AlertActions.displayAlert({
              title: 'Caretaker Application',
              body: 'You have applied to become the caretaker. Your request has been saved and will be reviewed by our staff.',
            }),
          ]),
          catchError((error) =>
            of(RatingActions.selectCaretakerBoondockFailed({ error })),
          ),
        ),
      ),
    ),
  { functional: true },
);
